import { useCallback, useEffect, useState } from 'react'
import { filterObjectByKey } from '../../shared/utils'
import {
    competitionActualitesGenerateUrl,
    competitionClassementGenerateUrl,
    competitionGenerateUrl,
    competitionRechercheGenerateUrl,
    competitionRencontreGenerateUrl,
    competitionStatistiquesGenerateUrl,
} from '../../shared/competitionUrls'
import { PageType, getCompetitionPageType } from '../../competitions/types'

export interface Params {
    ext_saison_id: string
    url_competition_type: string
    url_structure: string
    url_competition: string
    ext_poule_id: string
    numero_journee: string
    ext_equipe_id: string
    ext_rencontre_id: string
}

export const DEFAULT_PARAMS: Params = {
    ext_saison_id: '',
    url_competition_type: '',
    url_structure: '',
    url_competition: '',
    ext_poule_id: '',
    numero_journee: '',
    ext_equipe_id: '',
    ext_rencontre_id: '',
}

const PAGE_TYPE_URL_STRATEGIES: {
    [x in PageType]: (params: Partial<Params>) => string
} = {
    tunnel: competitionGenerateUrl,
    competition: competitionGenerateUrl,
    classement: competitionClassementGenerateUrl,
    statistiques: competitionStatistiquesGenerateUrl,
    recherche: competitionRechercheGenerateUrl,
    actualites: competitionActualitesGenerateUrl,
    rencontre: competitionRencontreGenerateUrl,
}

export const PARAMS_ATTRIBUTES = Object.keys(DEFAULT_PARAMS)

export const keepOnlyParams = <T>(paramsWithAttributes: T & Params): Params => {
    return filterObjectByKey(paramsWithAttributes, (key) =>
        PARAMS_ATTRIBUTES.includes(key),
    ) as Params
}

const generateHistoryState = <T>(params: T): T & { competitionParams: true } => {
    return {
        ...params,
        competitionParams: true,
    }
}

// todo: I think T can be Params...
export const useParamsHandler = <T>({
    initParams,
    onFinish,
}: {
    initParams: T
    onFinish?: ({ message }: { message: 'URL_NOT_CHANGED' }) => void
}): [T, (newParams: Params) => void] => {
    const pageType = getCompetitionPageType()
    const [params, _setParams] = useState<T>(initParams)

    const onPopState = useCallback(
        (e: PopStateEvent) => {
            // Update only if it's a competition params state
            if (e.state?.competitionParams) {
                _setParams(e.state)
            }
        },
        [_setParams],
    )

    useEffect(() => {
        window.addEventListener('popstate', onPopState)
        return () => {
            window.removeEventListener('popstate', onPopState)
        }
    }, [onPopState])

    const setParams = useCallback(
        (newParams: Params) => {
            const generatedUrl = PAGE_TYPE_URL_STRATEGIES[pageType](newParams).toLowerCase()
            // Avoid new query if is not needed
            if (generatedUrl === window.location.pathname) {
                onFinish?.({ message: 'URL_NOT_CHANGED' })
                return
            }

            const actualState = window.history.state
            // If state is not actually defined, it's the first click, we have to set the state in the actual history to handle the BACK click
            if (!actualState?.competitionParams) {
                window.history.replaceState(generateHistoryState(initParams), '')
            }

            const futureState = generateHistoryState(newParams)

            window.history.pushState(futureState, '', generatedUrl)
            const popStateEvent = new PopStateEvent('popstate', { state: futureState })
            dispatchEvent(popStateEvent)
        },
        [onFinish, pageType, initParams],
    )

    return [params, setParams]
}
